import { defineStore } from 'pinia';
import { useUpdateStatusStore } from './updateStatus';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, Ref } from 'vue';
import {
  CriteriaDTO,
  CriteriaGroupDTO,
  ICriteriaCountTeamLevelDTO,
  ICriteriaDTO,
  ICriteriaGroupDTO,
  IPaginationDTO,
  ITeamCriteriaSelectionDTO,
  ITeamLevelDTO,
  PaginationDTO,
  TeamCriteriaSelectionDTO,
  TeamLevelDTO,
} from '@/dtos';
import {
  assignTeamsCriteriaAdminAPI,
  createCriteriaAPI,
  createCriteriaGroupAPI,
  getCriteriaCountForTeamLevelsAPI,
  getCriteriaGroupsForYearAPI,
  getTeamsCriteriaAPI,
  removeCriteriaAPI,
  removeCriteriaGroupAPI,
  saveCriteriaCountForTeamLevelsAPI,
  updateCriteriaAPI,
  updateCriteriaGroupAPI,
  updateCriteriaGroupsAPI,
} from '@/services/admin/';
import { useAuthStore } from './auth';
import {
  assignTeamsCriteriaAPI,
  getCriteriaCountForTeamLevelYearAPI,
  getCriteriaForBranchYearAPI,
  getCriteriaForCriteriaGroupBranchYearAPI,
  getCriteriaForYearAPI,
  getMyCriteriaAPI,
  getRemainingCriteriaDataAPI,
} from '@/services';

export const useCriteriaStore = defineStore('critera', () => {
  const statusStore = useUpdateStatusStore();
  const queryClient = useQueryClient();
  const authStore = useAuthStore();

  const getCriteriaForYearQuery = (year: Ref<string>, pagination?: Ref<IPaginationDTO>) => {
    return useQuery({
      queryKey: computed(() => ['criteria-year', year.value, pagination?.value]),
      queryFn: async (): Promise<{ data: ICriteriaDTO[]; count: number }> => {
        const ret = await getCriteriaForYearAPI(
          year.value,
          pagination?.value ?? new PaginationDTO(),
        );
        const critera = ret.data.map((crit) => new CriteriaDTO(crit));

        return { data: critera, count: ret.count };
      },
      enabled: computed(() => !!year.value),
      refetchInterval: 10000,
    });
  };

  const getCriteriaForBranchYearQuery = (
    branchYearId: Ref<string>,
    pagination?: Ref<IPaginationDTO>,
  ) => {
    return useQuery({
      queryKey: computed(() => ['criteria-branch-year', branchYearId.value, pagination?.value]),
      queryFn: async (): Promise<{ data: ICriteriaGroupDTO[]; count: number }> => {
        const ret = await getCriteriaForBranchYearAPI(
          branchYearId.value,
          pagination?.value ?? new PaginationDTO(),
        );
        const critera = ret.data.map((crit) => new CriteriaGroupDTO(crit));
        return { data: critera, count: ret.count };
      },
      enabled: computed(() => !!branchYearId.value),
      refetchInterval: 10000,
    });
  };

  const getCriteriaForCriteriaGroupBranchYearQuery = (
    branchYearId: Ref<string>,
    criteriaGroupId: Ref<string>,
    pagination?: Ref<IPaginationDTO>,
  ) => {
    return useQuery({
      queryKey: computed(() => [
        'criteria-group-branch-year',
        branchYearId.value,
        criteriaGroupId.value,
      ]),
      queryFn: async (): Promise<{ data: CriteriaDTO[]; count: number }> => {
        const ret = await getCriteriaForCriteriaGroupBranchYearAPI(
          branchYearId.value,
          criteriaGroupId.value,
          pagination?.value ?? new PaginationDTO(),
        );
        const critera = ret.data.map((crit) => new CriteriaDTO(crit));
        return { data: critera, count: ret.count };
      },
      enabled: computed(() => !!branchYearId.value && !!criteriaGroupId.value),
    });
  };

  const getCriteriaGroupsForYearQuery = (year: Ref<string>, pagination?: Ref<IPaginationDTO>) => {
    return useQuery({
      queryKey: computed(() => ['criteria-year-group', year.value, pagination?.value]),
      queryFn: async (): Promise<{ data: ICriteriaGroupDTO[]; count: number }> => {
        const ret = await getCriteriaGroupsForYearAPI(
          year.value,
          pagination?.value ?? new PaginationDTO(-1),
        );
        const critera = ret.data.map((crit) => new CriteriaGroupDTO(crit));
        return { data: critera, count: ret.count };
      },
      enabled: computed(() => !!year.value),
    });
  };

  const getCriteriaCountForTeamLevels = (year: Ref<string>) => {
    return useQuery({
      queryKey: computed(() => ['criteria-year-team-levels', year.value]),
      queryFn: async (): Promise<{ data: ITeamLevelDTO[]; count: number }> => {
        const ret = await getCriteriaCountForTeamLevelsAPI(year.value);
        const critera = ret.data.map((crit) => new TeamLevelDTO(crit));
        return { data: critera, count: ret.count };
      },
      enabled: computed(() => !!year.value),
    });
  };

  const getCriteriaCountForTeamLevelYear = (year: Ref<string>, teamLevelId: Ref<string>) => {
    return useQuery({
      queryKey: computed(() => ['criteria-year-team-level', year.value, teamLevelId.value]),
      queryFn: async (): Promise<ICriteriaCountTeamLevelDTO> => {
        const ret = await getCriteriaCountForTeamLevelYearAPI(year.value, teamLevelId.value);
        return ret;
      },
      enabled: computed(() => !!year.value && !!teamLevelId.value),
    });
  };

  const getTeamsCriteriaQuery = (yearId: Ref<string>, teamId: Ref<string>) => {
    return useQuery({
      queryKey: computed(() => ['team-criteria', yearId.value, teamId.value]),
      queryFn: async (): Promise<{ data: ITeamCriteriaSelectionDTO[]; count: number }> => {
        let critera: ITeamCriteriaSelectionDTO[] = [];
        let count = 0;
        let ret;
        if (authStore.isUser) {
          ret = await getMyCriteriaAPI(yearId.value, teamId.value);
        } else {
          ret = await getTeamsCriteriaAPI(yearId.value, teamId.value);
        }
        critera = ret.data.map((crit) => new TeamCriteriaSelectionDTO(crit));
        count = ret.count;
        return { data: critera, count: count };
      },
      enabled: computed(() => !!yearId.value && !!teamId.value),
    });
  };

  const getRemainingCriteriaData = (branchYearId: Ref<string>) => {
    return useQuery({
      queryKey: computed(() => ['criteria-year-branch-remaining', branchYearId.value]),
      queryFn: async (): Promise<{ groupId: string; count: number }[]> => {
        const ret = await getRemainingCriteriaDataAPI(branchYearId.value);
        return ret;
      },
      enabled: computed(() => !!branchYearId.value),
      refetchInterval: 10000,
    });
  };

  // Mutations
  const createCriteria = useMutation({
    mutationFn: ({ yearId, criteria }: { yearId: string; criteria: ICriteriaDTO }) => {
      return createCriteriaAPI(yearId, criteria);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['criteria-year'] });
      statusStore.setSuccessStatus('criteria-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`criteria-create`, err);
    },
  });

  const updateCriteria = useMutation({
    mutationFn: (criteria: ICriteriaDTO) => {
      return updateCriteriaAPI(criteria);
    },
    onSuccess: (_data, criteria: ICriteriaDTO) => {
      queryClient.invalidateQueries({ queryKey: ['criteria-year'] });
      statusStore.setSuccessStatus(`criteria-${criteria.id}`, 'success:update');
    },
    onError: (err: string, criteria: ICriteriaDTO) => {
      statusStore.setErrorStatus(`criteria-${criteria.id}`, err);
    },
  });

  const removeCriteria = useMutation({
    mutationFn: (criteriaId: string) => {
      return removeCriteriaAPI(criteriaId);
    },
    onSuccess: (_data, criteriaId: string) => {
      queryClient.invalidateQueries({ queryKey: ['criteria-year'] });
      statusStore.setSuccessStatus(`criteria-${criteriaId}`, 'success:delete');
    },
    onError: (err: string, criteriaId: string) => {
      statusStore.setErrorStatus(`criteria-${criteriaId}`, err);
    },
  });

  const createGroupCriteria = useMutation({
    mutationFn: ({
      yearId,
      criteriaGroup,
    }: {
      yearId: string;
      criteriaGroup: ICriteriaGroupDTO;
    }) => {
      return createCriteriaGroupAPI(yearId, criteriaGroup);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['criteria-year-group'] });
      statusStore.setSuccessStatus('criteriaGroup-create', 'success:create');
    },
    onError: (err: string) => {
      statusStore.setErrorStatus(`criteriaGroup-create`, err);
    },
  });

  const updateGroupCriteria = useMutation({
    mutationFn: (criteriaGroup: ICriteriaGroupDTO) => {
      return updateCriteriaGroupAPI(criteriaGroup);
    },
    onSuccess: (_data, criteriaGroup: ICriteriaGroupDTO) => {
      queryClient.invalidateQueries({ queryKey: ['criteria-year-group'] });
      statusStore.setSuccessStatus(`criteriaGroup-${criteriaGroup.id}`, 'success:update');
    },
    onError: (err: string, criteriaGroup: ICriteriaGroupDTO) => {
      statusStore.setErrorStatus(`criteriaGroup-${criteriaGroup.id}`, err);
    },
  });

  const removeCriteriaGroup = useMutation({
    mutationFn: (criteriaGroupId: string) => {
      return removeCriteriaGroupAPI(criteriaGroupId);
    },
    onSuccess: (_data, criteriaGroupId: string) => {
      queryClient.invalidateQueries({ queryKey: ['criteria-year-group'] });
      statusStore.setSuccessStatus(`criteriaGroup-${criteriaGroupId}`, 'success:delete');
    },
    onError: (err: string, criteriaGroupId: string) => {
      statusStore.setErrorStatus(`criteriaGroup-${criteriaGroupId}`, err);
    },
  });

  const updateCriteriaGroups = useMutation({
    mutationFn: ({ yearId, groups }: { yearId: string; groups: ICriteriaGroupDTO[] }) => {
      return updateCriteriaGroupsAPI(yearId, groups);
    },
    onSuccess: (_data, variables: any) => {
      queryClient.invalidateQueries({ queryKey: ['criteria-year-group'] });
      statusStore.setSuccessStatus(`criteriaGroups-${variables.yearId}`, 'success:delete');
    },
    onError: (err: string, variables: any) => {
      statusStore.setErrorStatus(`criteriaGroup-${variables.yearId}`, err);
    },
  });

  const assignTeamCriteria = useMutation({
    mutationFn: ({
      yearId,
      teamId,
      criterias,
    }: {
      yearId: string;
      teamId: string;
      criterias: ITeamCriteriaSelectionDTO[];
    }) => {
      let ret;
      if (authStore.isAdmin) {
        ret = assignTeamsCriteriaAdminAPI(yearId, teamId, criterias);
      } else {
        ret = assignTeamsCriteriaAPI(yearId, teamId, criterias);
      }

      return ret;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['team-criteria', variables.yearId, variables.teamId],
      });
      queryClient.invalidateQueries({ queryKey: ['criteria-branch-year'] });
      queryClient.invalidateQueries({ queryKey: ['my-registration'] });
      if (_data == false) {
        statusStore.setErrorStatus(
          `assign-criteria-${variables.teamId}-${variables.yearId}`,
          'Selected Criteria can no longer be choosen.',
        );
      } else {
        statusStore.setSuccessStatus(
          `assign-criteria-${variables.teamId}-${variables.yearId}`,
          'success:update',
        );
      }
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`assign-criteria-${variables.teamId}-${variables.yearId}`, err);
    },
  });

  const saveTeamLevelCriteria = useMutation({
    mutationFn: ({
      yearId,
      teamLevelCriteria,
    }: {
      yearId: string;
      teamLevelCriteria: ITeamLevelDTO[];
    }) => {
      const ret = saveCriteriaCountForTeamLevelsAPI(yearId, teamLevelCriteria);
      return ret;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['criteria-year-team-levels', variables.yearId],
      });
      statusStore.setSuccessStatus(
        `criteria-year-team-levels-${variables.yearId}`,
        'success:update',
      );
    },
    onError: (err: string, variables) => {
      statusStore.setErrorStatus(`criteria-year-team-levels-${variables.yearId}`, err);
    },
  });

  const invalidateCriteriaGroups = async () => {
    queryClient.invalidateQueries({ queryKey: ['criteria-year-group'] });
  };

  const invalidateCriteria = async () => {
    queryClient.invalidateQueries({ queryKey: ['criteria-year'] });
  };

  return {
    getCriteriaForYearQuery,
    getCriteriaForBranchYearQuery,
    getCriteriaGroupsForYearQuery,
    getCriteriaCountForTeamLevels,
    getCriteriaCountForTeamLevelYear,
    getRemainingCriteriaData,
    getCriteriaForCriteriaGroupBranchYearQuery,
    createCriteria,
    updateCriteria,
    removeCriteria,
    createGroupCriteria,
    updateGroupCriteria,
    removeCriteriaGroup,
    assignTeamCriteria,
    saveTeamLevelCriteria,
    updateCriteriaGroups,
    invalidateCriteriaGroups,
    invalidateCriteria,
    getTeamsCriteriaQuery,
  };
});
